import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, Menu, Phone, X } from 'lucide-react';
import { NAV_LINKS, COMPANY } from '../data/content';
import { EASE } from './motion';
import { Logo } from './Logo';

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-paperline shadow-sm' : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0 mr-6 xl:mr-10" aria-label="Ehimo Building & Civil Engineering — Home">
            <span className={`block transition-transform duration-500 hover:scale-[1.03] ${scrolled ? '' : 'bg-white rounded-md px-2.5 py-1.5'}`}>
              <Logo className="h-9 sm:h-10 xl:h-11" />
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-9" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[12px] xl:text-[13px] uppercase tracking-[0.14em] xl:tracking-[0.18em] whitespace-nowrap transition-colors duration-300 hover:text-accent ${
                    isActive ? (scrolled ? 'text-brand font-semibold' : 'text-accent') : (scrolled ? 'text-ink/75' : 'text-white/90')
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={COMPANY.phoneHrefs[0]}
              data-testid="nav-phone"
              className={`hidden xl:inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.18em] ${scrolled ? 'text-ink/75' : 'text-white/90'} hover:text-accent transition-colors duration-300`}
            >
              <Phone size={14} /> {COMPANY.phones[0]}
            </a>
            <Link
              to="/contact"
              data-testid="start-project-cta"
              className="hidden lg:inline-flex items-center gap-2 bg-accent text-ink font-display font-medium text-sm uppercase tracking-wide px-5 xl:px-6 py-3 whitespace-nowrap transition-colors duration-300 hover:bg-accent-dim"
            >
              Start a Project <ArrowUpRight size={16} />
            </Link>
            <button
              data-testid="menu-toggle"
              onClick={() => setOpen(!open)}
              className={`lg:hidden p-2 ${scrolled ? 'text-ink' : 'text-white'}`}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 bg-ink flex flex-col justify-between pt-28 pb-10 px-6"
            initial={{ opacity: 0, y: '-4%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '-4%' }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.5, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `font-display text-4xl font-semibold uppercase tracking-tight py-2 block transition-colors ${
                        isActive ? 'text-accent' : 'text-white'
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="border-t border-line pt-6 text-sm text-mute space-y-2"
            >
              <p>{COMPANY.address}</p>
              <a href={COMPANY.phoneHrefs[0]} className="block text-white" data-testid="mobile-menu-phone">{COMPANY.phones[0]}</a>
              <a href={`mailto:${COMPANY.email}`} className="block text-accent" data-testid="mobile-menu-email">{COMPANY.email}</a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
